const BASE32 = "0123456789bcdefghjkmnpqrstuvwxyz";

function normalizeString(value) {
  return String(value || "").trim();
}

function toFiniteNumber(value) {
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
}

function resolveLatLng(row = {}) {
  const latitude = toFiniteNumber(
    row.lat ?? row.latitude ?? row.location?.lat ?? row.location?.latitude ?? row.coordinates?.lat
  );
  const longitude = toFiniteNumber(
    row.lng ?? row.longitude ?? row.location?.lng ?? row.location?.longitude ?? row.coordinates?.lng
  );
  if (latitude === null || longitude === null) return { latitude: null, longitude: null };
  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return { latitude: null, longitude: null };
  }
  return { latitude, longitude };
}

export function encodeGeohash(latitude, longitude, precision = 9) {
  const lat = toFiniteNumber(latitude);
  const lng = toFiniteNumber(longitude);
  const length = Math.max(1, Math.min(12, Number(precision) || 9));
  if (lat === null || lng === null || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return "";
  }

  let idx = 0;
  let bit = 0;
  let evenBit = true;
  let geohash = "";
  const latRange = [-90, 90];
  const lngRange = [-180, 180];

  while (geohash.length < length) {
    if (evenBit) {
      const mid = (lngRange[0] + lngRange[1]) / 2;
      if (lng >= mid) {
        idx = idx * 2 + 1;
        lngRange[0] = mid;
      } else {
        idx *= 2;
        lngRange[1] = mid;
      }
    } else {
      const mid = (latRange[0] + latRange[1]) / 2;
      if (lat >= mid) {
        idx = idx * 2 + 1;
        latRange[0] = mid;
      } else {
        idx *= 2;
        latRange[1] = mid;
      }
    }

    evenBit = !evenBit;
    bit += 1;
    if (bit === 5) {
      geohash += BASE32.charAt(idx);
      bit = 0;
      idx = 0;
    }
  }

  return geohash;
}

export function normalizeCampaignLocation(row = {}) {
  const id = normalizeString(row.id || row.locationId);
  const name = normalizeString(row.name) || (row.isHq ? "HQ" : "Location");
  const address = normalizeString(row.address || row.formattedAddress || row.hqAddress);
  const status = normalizeString(row.status || "active").toLowerCase();
  if (!id || status === "archived" || status === "deleted") return null;

  const { latitude, longitude } = resolveLatLng(row);
  const geohash =
    latitude !== null && longitude !== null
      ? normalizeString(row.geohash) || encodeGeohash(latitude, longitude)
      : "";

  return {
    id,
    name,
    address,
    isHq: row.isHq === true,
    ...(latitude !== null && longitude !== null
      ? { lat: latitude, lng: longitude, geohash }
      : {}),
  };
}

export function normalizeCampaignLocationSelection({
  scope = "all_locations",
  selectedLocationIds = [],
  businessLocations = [],
} = {}) {
  const normalizedLocations = (Array.isArray(businessLocations) ? businessLocations : [])
    .map(normalizeCampaignLocation)
    .filter(Boolean);
  const requestedScope =
    scope === "selected_locations" ? "selected_locations" : "all_locations";
  const selectedIds = Array.from(
    new Set((Array.isArray(selectedLocationIds) ? selectedLocationIds : []).map(normalizeString).filter(Boolean))
  );
  const selectedIdSet = new Set(selectedIds);
  const validLocations =
    requestedScope === "selected_locations"
      ? normalizedLocations.filter((location) => selectedIdSet.has(location.id))
      : normalizedLocations;
  const geohashes = Array.from(
    new Set(validLocations.map((location) => normalizeString(location.geohash)).filter(Boolean))
  );

  return {
    validLocationScope: requestedScope,
    validLocationIds:
      requestedScope === "selected_locations"
        ? validLocations.map((location) => location.id)
        : [],
    validLocations,
    validLocationGeohashes: geohashes,
    geocodedValidLocationCount: geohashes.length,
  };
}

export function buildRaffleLocationIndexRows(raffle = {}) {
  const campaignId = normalizeString(raffle.id);
  if (!campaignId) return [];
  return (Array.isArray(raffle.validLocations) ? raffle.validLocations : [])
    .map(normalizeCampaignLocation)
    .filter((location) => location?.geohash)
    .map((location) => ({
      id: `${campaignId}_${location.id}`,
      campaignId,
      campaignType: normalizeString(raffle.campaignType || "raffle") || "raffle",
      businessId: normalizeString(raffle.businessId),
      locationId: location.id,
      locationName: location.name,
      address: location.address,
      lat: location.lat,
      lng: location.lng,
      geohash: location.geohash,
      status: normalizeString(raffle.status || "draft") || "draft",
      title: normalizeString(raffle.title),
      slug: normalizeString(raffle.slug),
      heroImageUrl: normalizeString(raffle.heroImageUrl),
      claimStartAt: raffle.claimStartAt || null,
      claimEndAt: raffle.claimEndAt || null,
      publicClaimPath: normalizeString(raffle.publicClaimPath),
      publicClaimUrl: normalizeString(raffle.publicClaimUrl),
    }));
}
